import React, { useState } from 'react';
import { useLMS } from '../../context/LMSContext';
import { authHelpers } from '../../lib/supabase';

const LoginPage = () => {
  const { login, isDarkMode } = useLMS();
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isForgotPassword) {
        if (!email) {
          setError('Please enter your email address');
          setIsLoading(false);
          return;
        }
        try {
          const { error: resetError } = await authHelpers.resetPassword(email);
          if (resetError) {
            setError(resetError.message);
          } else {
            setResetSent(true);
          }
        } catch (err) {
          setError('Failed to send reset email');
        }
        setIsLoading(false);
        return;
      }

      if (!email || !password) {
        setError('Please enter email and password');
        setIsLoading(false);
        return;
      }
      console.log('Attempting login...');
      const result = await login(email, password);
      console.log('Login result:', result);
      if (!result.success) {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const switchToLogin = () => {
    setIsForgotPassword(false);
    setError('');
    setResetSent(false);
  };

  const switchToForgotPassword = () => {
    setIsForgotPassword(true);
    setError('');
    setResetSent(false);
  };

  return (
    <div className={`login-page ${isDarkMode ? 'dark' : ''}`}>
      {/* Header with Logo */}
      <header className="login-page-header">
        <img 
          src="https://cdn.shopify.com/s/files/1/0659/6208/2392/files/Modern_Logo_for_SimDebrief_with_Dialogue_Bubble_280_x_70_px_1.png?v=1769253766" 
          alt="Code Breakers NCLEX"
          className="header-logo"
        />
      </header>

      <div className="login-content">
        <div className="login-left">
          <div className="login-form-container">
            <form className="login-form" onSubmit={handleSubmit}>
              <h2>{isForgotPassword ? 'Reset Password' : 'Welcome Back'}</h2>
              <p>
                {isForgotPassword 
                  ? 'Enter your email and we\'ll send you a reset link'
                  : 'Sign in to continue your NCLEX prep journey'}
              </p>
              
              {error && (
                <div className="form-error">
                  {error}
                </div>
              )}

              {resetSent && (
                <div className="form-success">
                  ✅ Password reset email sent! Check your inbox.
                </div>
              )}
              
              <div className="form-group">
                <label>Email Address</label>
                <input 
                  type="email" 
                  placeholder="student@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              
              {!isForgotPassword && (
                <div className="form-group">
                  <label>Password</label>
                  <input 
                    type="password" 
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              )}
              
              <button type="submit" className="login-btn" disabled={isLoading}>
                {isLoading 
                  ? '⏳ Please wait...' 
                  : isForgotPassword 
                    ? 'Send Reset Link' 
                    : 'Sign In'}
              </button>
              
              <div className="login-footer">
                {isForgotPassword ? (
                  <>Remember your password? <a href="#" onClick={(e) => { e.preventDefault(); switchToLogin(); }}>Sign in</a></>
                ) : (
                  <a href="#" onClick={(e) => { e.preventDefault(); switchToForgotPassword(); }} className="forgot-link">Forgot your password?</a>
                )}
              </div>
            </form>
          </div>
        </div>
        
        <div className="login-right">
          <div className="login-features">
            <h3>Break the Code to NCLEX Success</h3>
            
            <div className="feature-item">
              <div className="feature-icon">🎓</div>
              <div className="feature-text">
                <h4>Live Instruction</h4>
                <p>6 weeks of live lectures, Monday, Wednesday, and Friday 11:30am-3pm EST</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">📚</div>
              <div className="feature-text">
                <h4>Structured Modules</h4>
                <p>Weekly content including videos, assignments, and practice questions</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">📅</div>
              <div className="feature-text">
                <h4>Calendar Integration</h4>
                <p>Stay on track with integrated assignment due dates and lecture reminders</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">👥</div>
              <div className="feature-text">
                <h4>Community Support</h4>
                <p>Connect with fellow students in our private Facebook group</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
